"use client";
import { Alert, AlertTitle, AlertDescription } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { toast } from "components/ui/use-toast";
import { Label } from "@radix-ui/react-label";
import { Separator } from "@radix-ui/react-select";
import { AlertCircle } from "lucide-react";
import { useReCaptcha } from "next-recaptcha-v3";
import { useRef, useState } from "react";

const Page = () => {
  const email = useRef<string>("");
  const [isResetPasswordSuccessful, setIsResetPasswordSuccessful] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Import 'executeRecaptcha' using 'useReCaptcha' hook
  const { executeRecaptcha } = useReCaptcha();

  const handleChangeEmail = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    email.current = value;
  };

  const handleInitiateResetPassword = async () => {
    if (
      !email.current ||
      !email.current
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      toast({
        duration: 1000,
        description: "Email invalide",
        variant: "destructive",
      });

      return;
    }

    // Generate ReCaptcha token
    const token = await executeRecaptcha("form_submit");

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/authentication/initiate-reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            email: email.current,
            token,
          }),
        }
      );

      const data = await response.json();

      if (data.statusCode === 422) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      toast({
        duration: 1000,
        description: "On vient de t'envoyer un mail de réinitialisation",
      });

      setIsResetPasswordSuccessful(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen anim_gradient">
      <div className="p-4 space-y-4 lg:w-1/3 lg:mx-auto bg-background h-full">
        <h1 className="text-2xl lg:text-3xl">{"Mot de passe oublié"}</h1>

        <Separator className="w-full" />

        {isResetPasswordSuccessful && (
          <Alert variant="default" className="border-primary text-primary">
            <AlertCircle className="h-4 w-4 stroke-primary" />
            <AlertTitle>{"Le mail est parti !"}</AlertTitle>
            <AlertDescription>
              <p>
                {
                  "Un email de réinitialisation de mot de passe a été envoyé à ton adresse mail, vérifie bien les spams au cas où"
                }
              </p>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">{"Ton adresse mail :"}</Label>

          <Input
            type="email"
            id="email"
            onChange={handleChangeEmail}
            placeholder="alice@example.com"
          />
        </div>

        <Button
          onClick={handleInitiateResetPassword}
          className="p-2"
          disabled={isLoading}
        >
          {"Réinitialiser mon mot de passe"}
        </Button>
      </div>
    </div>
  );
};

export default Page;
